/// <amd-module name="Feed/Legacy/Components/Settings/AddUpstreamProtocolSelection" />
import * as React from "react";

import { Label } from "OfficeFabric/Label";

import { Checkbox } from "VSSUI/Checkbox";

import * as PackageResources from "Feed/Common/Resources";
import { IInternalUpstreamSettingsState } from "Feed/Legacy/Components/Settings/IFeedSettingsState";
import { MavenKeyCapitalized } from "Feed/Legacy/Protocols/Maven/Constants/MavenConstants";
import { NpmKey } from "Feed/Legacy/Protocols/Npm/Constants/NpmConstants";
import { NuGetKeyCapitalized } from "Feed/Legacy/Protocols/NuGet/Constants/NuGetConstants";
import { PyPiKey } from "Feed/Legacy/Protocols/PyPi/PyPiConstants";
import { UPackKeyCapitalized } from "Feed/Legacy/Protocols/UPack/UPackConstants";

export interface IAddUpstreamProtocolSelectionProps {
    /**
     * Callback to be used when the selected protocols have changed
     */
    selectedProtocolsChangedHandler: (protocols: string[]) => void;

    /**
     * Internal upstream feature availability
     */
    internalUpstreamSettings: IInternalUpstreamSettingsState;

    /**
     * UPack same collection upstream feature availability
     */
    isUPackSameCollectionUpstreamAvailable: boolean;

    /**
     * UPack cross collection upstream feature availability
     */
     isUPackCrossCollectionUpstreamAvailable: boolean;
}

export interface IAddUpstreamProtocolSelectionState {
    /**
     * Dictionary informing if a particular protocol is selected
     */
    selectedProtocolsDictionary: { [protocol: string]: boolean };
}

/**
 * Panel that allows the user to specify an upstream source for addition to the feed
 */
export class AddUpstreamProtocolSelection extends React.Component<
    IAddUpstreamProtocolSelectionProps,
    IAddUpstreamProtocolSelectionState
> {
    constructor(props: IAddUpstreamProtocolSelectionProps) {
        super(props);
        this.state = {
            selectedProtocolsDictionary: {}
        };
    }

    public render(): JSX.Element {
        return (
            <div>
                <div className="labeled-callout">
                    <Label className="locator-label" required={true}>
                        {PackageResources.AddUpstream_ProtocolMultiselectionLabel}
                    </Label>
                </div>
                <Checkbox
                    label={NpmKey}
                    checked={this.state.selectedProtocolsDictionary[NpmKey]}
                    onChange={(
                        ev: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
                        checked: boolean
                    ) => this._onCheckboxChange(ev, checked, NpmKey)}
                />
                <Checkbox
                    label={NuGetKeyCapitalized}
                    checked={this.state.selectedProtocolsDictionary[NuGetKeyCapitalized]}
                    onChange={(
                        ev: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
                        checked: boolean
                    ) => this._onCheckboxChange(ev, checked, NuGetKeyCapitalized)}
                />
                <Checkbox
                    label="Python"
                    checked={this.state.selectedProtocolsDictionary[PyPiKey]}
                    onChange={(
                        ev: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
                        checked: boolean
                    ) => this._onCheckboxChange(ev, checked, PyPiKey)}
                />
                <Checkbox
                    label={MavenKeyCapitalized}
                    checked={this.state.selectedProtocolsDictionary[MavenKeyCapitalized]}
                    onChange={(
                        ev: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
                        checked: boolean
                    ) => this._onCheckboxChange(ev, checked, MavenKeyCapitalized)}
                />
                {(this.props.isUPackSameCollectionUpstreamAvailable || this.props.isUPackCrossCollectionUpstreamAvailable) && (
                    <Checkbox
                        label={UPackKeyCapitalized}
                        checked={this.state.selectedProtocolsDictionary[UPackKeyCapitalized]}
                        onChange={(
                            ev: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
                            checked: boolean
                        ) => this._onCheckboxChange(ev, checked, UPackKeyCapitalized)}
                    />
                )}
            </div>
        );
    }

    private _onCheckboxChange = (
        ev: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
        checked: boolean,
        protocol: string
    ): void => {
        this.setState(
            (prevState, props) => {
                prevState.selectedProtocolsDictionary[protocol] = checked;
                return { selectedProtocolsDictionary: prevState.selectedProtocolsDictionary };
            },
            () => {
                const protocols: string[] = [];
                for (const protocolKey in this.state.selectedProtocolsDictionary) {
                    if (this.state.selectedProtocolsDictionary[protocolKey] === true) {
                        protocols.push(protocolKey);
                    }
                }

                this.props.selectedProtocolsChangedHandler(protocols);
            }
        );
    };
}
